import * as actions from './actions';
import {
    Banner,
    InlineConfig,
    Link,
    LockedPricingBotConfig,
    SetVisitorDataConfig,
    VisitorConfig,
} from './typedefs/external';
import { DICWindow } from './typedefs/window';

declare var window: DICWindow;

/**
 * These define the official "API" of the embed. Websites can call these methods.
 */
export function defineExternalApi(store: any) {
    // this is the function that sites can call to start the locked pricing bot
    window.DIConv.startUnlockPricingBot = (config: LockedPricingBotConfig) => {
        validateLockedPricingConfig(config);
        store.dispatch(actions.startLockedPricingBot(config));
    };

    // this is the function that sites can call to start a convo programatically
    window.DIConv.startConversation = () => {
        store.dispatch(actions.ctaClicked());
    };

    // this is the function that sites can call to start a convo programatically with an initial visitor message
    window.DIConv.startConversationWithVisitorMessage = (initialMessage: string) => {
        store.dispatch(actions.startConvoWithMessage(initialMessage));
    };

    // this is the function that sites can call to set visitor data programatically
    window.DIConv.setVisitorData = (userData: SetVisitorDataConfig) => {
        store.dispatch(actions.contactInfoInjected(userData));
    };

    // this is the function that sites can call to set custom metadata programatically
    window.DIConv.setCustomMetadata = (metadata: string) => {
        store.dispatch(actions.customMetadataInjected(metadata));
    };
}

/**
 * These define the deprecated "API" of the embed. Websites can call these methods but they will be removed.
 */
export function defineLegacyApi(store: any) {
    if (!window.Conversations) {
        window.Conversations = {
            openStartPanel: () => {
                console.warn(
                    'Conversations.openStartPanel is deprecated. You should use DIConv.startConversation() instead.',
                );
                reportLegacyUsage('openStartPanel');
                window.DIConv.startConversation();
            },
            setUserData: (data: SetVisitorDataConfig) => {
                console.warn(
                    'Conversations.setUserData is deprecated. You should use config options or DIConv.setVisitorData() instead.',
                );
                reportLegacyUsage('setUserData');
                window.DIConv.setVisitorData(data);
            },
            setFirstName: (firstName: string) => {
                console.warn(
                    'Conversations.setFirstName is deprecated. You should use config options or DIConv.setVisitorData() instead.',
                );
                reportLegacyUsage('setFirstName');
                window.DIConv.setVisitorData({ firstName });
            },
            setLastName: (lastName: string) => {
                console.warn(
                    'Conversations.setLastName is deprecated. You should use config options or DIConv.setVisitorData() instead.',
                );
                reportLegacyUsage('setLastName');
                window.DIConv.setVisitorData({ lastName });
            },
            setEmail: (email: string) => {
                console.warn(
                    'Conversations.setEmail is deprecated. You should use config options or DIConv.setVisitorData() instead.',
                );
                reportLegacyUsage('setEmail');
                window.DIConv.setVisitorData({ email });
            },
            addStarter: (args: any) => {
                // tslint:disable-next-line
                console.error(
                    'Conversations.addStarter is not supported. You should use config options instead.',
                );
                reportLegacyUsage('addStarter');
            },
            addSecondaryStarter: (args: any) => {
                // tslint:disable-next-line
                console.error(
                    'Conversations.addSecondaryStarter is not supported. You should use config options instead.',
                );
                reportLegacyUsage('addSecondaryStarter');
            },
            addBanner: (banners: Banner | Banner[]) => {
                // tslint:disable-next-line
                console.warn(
                    'Conversations.addBanner is deprecated. You should use config options instead.',
                );
                reportLegacyUsage('addBanner');
                if (!Array.isArray(banners)) banners = [banners];
                const errors = validateBanners(banners);
                if (errors.length > 0) {
                    // tslint:disable-next-line
                    console.error(errors);
                    return;
                }
                store.dispatch(actions.bannerInjected(banners));
            },
            addLink: (links: Link | Link[]) => {
                // tslint:disable-next-line
                console.warn(
                    'Conversations.addLink is deprecated. You should use config options instead.',
                );
                reportLegacyUsage('addLink');
                if (!Array.isArray(links)) links = [links];
                const errors = validateLinks(links);
                if (errors.length > 0) {
                    // tslint:disable-next-line
                    console.error(errors);
                    return;
                }
                store.dispatch(actions.linkInjected(links));
            },
            addJoiningMessage: (args: any) => {
                // tslint:disable-next-line
                console.error('Conversations.addJoiningMessage is not supported.');
                reportLegacyUsage('addJoiningMessage');
            },
            addJoiningAnonymousMessages: (args: any) => {
                // tslint:disable-next-line
                console.error('Conversations.addJoiningAnonymousMessages is not supported.');
                reportLegacyUsage('addJoiningAnonymousMessages');
            },
            addAgentSearchingMessages: (args: any) => {
                // tslint:disable-next-line
                console.error('Conversations.addAgentSearchingMessages is not supported.');
                reportLegacyUsage('addAgentSearchingMessages');
            },
            hideConversationsDisplay: () => {
                // tslint:disable-next-line
                console.warn(
                    'Conversations.hideConversationsDisplay is deprecated.  You should use config options instead.',
                );
                reportLegacyUsage('hideConversationsDisplay');
                store.dispatch(actions.hideCta());
            },
            setCustomNote: (customMetadata: string) => {
                // tslint:disable-next-line
                console.warn(
                    'Conversations.setCustomNote is deprecated. You should use config options or DIConv.setCustomMetadata() instead.',
                );
                reportLegacyUsage('setCustomNote');
                window.DIConv.setCustomMetadata(customMetadata);
            },
            setCtaText: (ctaText: string) => {
                // tslint:disable-next-line
                console.warn(
                    'Conversations.setCtaText is deprecated. You should use config options instead.',
                );
                reportLegacyUsage('setCtaText');
                store.dispatch(actions.setCtaText(ctaText));
            },
        };
    }
}

function validateLockedPricingConfig(config: LockedPricingBotConfig) {
    let errors = [];

    if (typeof config !== 'object') errors.push('Payload must be an object');

    // validate vehicleDetail
    if (typeof config.vehicle !== 'object') errors.push('Vehicle must be an object');
    if (!config.vehicle.title) errors.push('Vehicle title is required');
    if (!config.vehicle.stockNumber) errors.push('Vehicle stock number is required');
    if (!config.vehicle.price) {
        errors.push('Vehicle price is required');
    } else if (typeof config.vehicle.price !== 'number') {
        errors.push('Vehicle price must be a number');
    }
    if (config.vehicle.msrp && typeof config.vehicle.msrp !== 'number') {
        errors.push('Vehicle MSRP must be a number');
    }

    // throw Error if there are any validation errors
    if (errors.length > 0) throw new Error(errors.join(', '));
}

/**
 * If we were started with a config option, make sure it's valid
 */
export function validateStartConfig(config: InlineConfig): void {
    let errors: string[] = [];

    if (typeof config !== 'object') {
        errors.push(`You must provide a config object to DIConv.start(). Found ${typeof config}.`);
    }
    if (config.starters) {
        errors = errors.concat(validateStarters(config.starters));
    }
    if (config.waitingForAgentMessages) {
        errors = errors.concat(validateWaitingForAgentMessages(config.waitingForAgentMessages));
    }
    if (config.banners) errors = errors.concat(validateBanners(config.banners));
    if (config.links) errors = errors.concat(validateLinks(config.links));
    if (config.visitor) errors = errors.concat(validateVisitor(config.visitor));
    if (config.ctaText) errors = errors.concat(validateAsString(config.ctaText, 'ctaText'));
    if (config.ctaCustomImage) {
        errors = errors.concat(validateAsString(config.ctaCustomImage, 'ctaCustomImage'));
    }
    if (config.ctaBackgroundColor) {
        errors = errors.concat(validateAsString(config.ctaBackgroundColor, 'ctaBackgroundColor'));
    }
    if (config.ctaForegroundColor) {
        errors = errors.concat(validateAsString(config.ctaForegroundColor, 'ctaForegroundColor'));
    }
    if (config.ctaPosition) {
        errors = errors.concat(validateAsString(config.ctaPosition, 'ctaPosition'));
    }
    if (config.ctaTailBackgroundColor) {
        errors = errors.concat(
            validateAsString(config.ctaTailBackgroundColor, 'ctaTailBackgroundColor'),
        );
    }
    if (config.ctaTailForegroundColor) {
        errors = errors.concat(
            validateAsString(config.ctaTailForegroundColor, 'ctaTailForegroundColor'),
        );
    }
    if (config.accentColor1) {
        errors = errors.concat(validateAsString(config.accentColor1, 'accentColor1'));
    }
    if (config.accentColor2) {
        errors = errors.concat(validateAsString(config.accentColor2, 'accentColor2'));
    }
    if (config.ctaCustomImage) {
        errors = errors.concat(validateAsString(config.ctaCustomImage, 'ctaCustomImage'));
    }
    if (config.onReady) errors = errors.concat(validateOnReady(config.onReady));

    if (errors.length > 0) {
        let errorMessage =
            'Dealer Inspire Conversations was initiated with an invalid configuration.';
        errorMessage +=
            '\n----------------------------\nPROVIDED CONFIGURATION: \n\n' + JSON.stringify(config);
        errorMessage +=
            '\n----------------------------\nERRORS DETECTED:\n\n' +
            errors.map(e => `* ${e}`).join('\n') +
            '\n';
        throw new Error(errorMessage);
    }
}

function validateStarters(starters: string[]): Array<string> {
    const errors: string[] = [];

    if (!Array.isArray(starters)) {
        errors.push(`starters should be an array of strings. Found ${typeof starters}.`);
    } else {
        starters.forEach((starter, i) => {
            if (!starter || typeof starter !== 'string') {
                errors.push(
                    `starter text is required and must be a string. Found ${typeof starter} at index ${i}.`,
                );
            }
        });
    }

    return errors;
}

function validateWaitingForAgentMessages(waitingForAgentMessages: string[]): string[] {
    const errors: string[] = [];

    if (!Array.isArray(waitingForAgentMessages)) {
        errors.push(
            `waitingForAgentMessages should be an array of strings. Found ${typeof waitingForAgentMessages}.`,
        );
    } else {
        waitingForAgentMessages.forEach((waitingForAgentMessage, i) => {
            if (typeof waitingForAgentMessage !== 'string') {
                errors.push(
                    `waitingForAgentMessage should be a string. Found ${typeof waitingForAgentMessage} at index ${i}`,
                );
            }
        });
    }

    return errors;
}

function validateBanners(banners: Banner[]): string[] {
    const errors: string[] = [];

    if (!Array.isArray(banners)) {
        errors.push(`banners should be an array of Banner objects. Found ${typeof banners}.`);
    } else {
        banners.forEach((banner, i) => {
            if (typeof banner !== 'object') {
                errors.push(
                    `banner should be a Banner object. Found ${typeof banner} at index ${i}`,
                );
            } else {
                if (!banner.text || typeof banner.text !== 'string') {
                    errors.push(
                        `banner.text is required and must be a string. Found ${typeof banner.text} at index ${i}`,
                    );
                }
                if (!banner.url || typeof banner.url !== 'string') {
                    errors.push(
                        `banner.url is required and must be a string. Found ${typeof banner.url} at index ${i}`,
                    );
                }
            }
        });
    }

    return errors;
}

function validateLinks(links: Link[]): string[] {
    const errors: string[] = [];

    if (!Array.isArray(links)) {
        errors.push(`links should be an array of Link objects. Found ${typeof links}.`);
    } else {
        links.forEach((link, i) => {
            if (typeof link !== 'object') {
                errors.push(`link should be a Link object. Found ${typeof link} at index ${i}`);
            } else {
                if (!link.text || typeof link.text !== 'string') {
                    errors.push(
                        `link.text is required and must be a string. Found ${typeof link.text} at index ${i}`,
                    );
                }
                if (!link.url || typeof link.url !== 'string') {
                    errors.push(
                        `link.url is required and must be a string. Found ${typeof link.url} at index ${i}`,
                    );
                }
                if (!link.icon || typeof link.icon !== 'string') {
                    errors.push(
                        `link.icon is required and must be a string. Found ${typeof link.icon} at index ${i}`,
                    );
                }
            }
        });
    }

    return errors;
}

function validateVisitor(visitor: VisitorConfig): string[] {
    const errors: string[] = [];

    if (typeof visitor !== 'object') {
        errors.push(`visitor should be a Visitor object. Found ${typeof visitor}.`);
    }

    return errors;
}

function validateAsString(item: string, name: string): string[] {
    const errors: string[] = [];

    if (typeof item !== 'string') {
        errors.push(`${name} should be a string. Found ${typeof item}.`);
    }

    return errors;
}

function validateOnReady(onReady: () => void): string[] {
    const errors: string[] = [];

    if (typeof onReady !== 'function') {
        errors.push(`onReady should be a function. Found ${typeof onReady}.`);
    }

    return errors;
}

function reportLegacyUsage(method: string) {
    if (window.DIConv.Bugsnag) {
        window.DIConv.Bugsnag.notify(`Legacy Library Call: ${method}`, method, null, 'info');
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/defineExternalApi.ts